import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Users, Heart, Clock } from 'lucide-react';
import { businessInfo, photos, products } from '../data/mockData';

const Home = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[85vh] overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url(${photos.hero})`,
            backgroundPosition: 'center'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-wood-brown/90 via-wood-brown/70 to-transparent" />
        </div>
        
        <div className="relative container mx-auto px-4 h-full flex items-center">
          <div className="max-w-2xl animate-fadeIn">
            <div className="inline-block bg-burgundy/90 text-cream px-4 py-2 rounded-md mb-4 font-serif text-sm font-medium">
              Dal {businessInfo.foundedYear}
            </div>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-bold text-cream mb-6 leading-tight">
              Salumificio Lombardi 1957
            </h1>
            <p className="text-2xl md:text-3xl text-beige font-serif mb-8 leading-relaxed">
              {businessInfo.tagline}
            </p>
            <p className="text-lg text-cream/90 font-serif mb-10 leading-relaxed max-w-xl">
              Tre generazioni di passione, tradizione artigianale e qualità nella lavorazione di salumi e carni fresche a Santarcangelo di Romagna.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link 
                to="/prodotti"
                className="inline-flex items-center justify-center bg-burgundy text-cream px-8 py-4 rounded-md font-serif font-medium text-lg transition-all duration-300 hover:bg-burgundy-dark hover:shadow-xl hover:scale-105 group"
              >
                Scopri i Prodotti
                <ArrowRight size={20} className="ml-2 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link 
                to="/contatti"
                className="inline-flex items-center justify-center bg-cream/95 text-wood-brown px-8 py-4 rounded-md font-serif font-medium text-lg transition-all duration-300 hover:bg-cream hover:shadow-xl hover:scale-105"
              >
                Contattaci
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-cream rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-cream rounded-full" />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-20 bg-cream">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-burgundy mb-8">
              Benvenuti nel Nostro Salumificio
            </h2>
            <p className="text-lg text-wood-brown font-serif leading-relaxed mb-6">
              Il Salumificio Lombardi è un'azienda familiare che dal <strong>1957</strong> porta avanti con passione la tradizione romagnola nella lavorazione artigianale di salumi e carni fresche.
            </p>
            <p className="text-lg text-wood-brown font-serif leading-relaxed mb-8">
              A Santarcangelo di Romagna, a pochi chilometri da Rimini, la famiglia Lombardi continua a servire la comunità locale con la stessa dedizione e cura di tre generazioni fa, utilizzando solo <strong>materie prime di alta qualità</strong> provenienti dall'Emilia Romagna.
            </p>
          </div>

          {/* Values Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mt-16">
            <div className="text-center p-6 bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1">
              <div className="w-16 h-16 bg-burgundy/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award size={32} className="text-burgundy" />
              </div>
              <h3 className="font-serif text-xl font-bold text-burgundy mb-3">Qualità</h3>
              <p className="text-wood-brown font-serif text-sm">
                Solo materie prime selezionate dall'Emilia Romagna
              </p>
            </div>

            <div className="text-center p-6 bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1">
              <div className="w-16 h-16 bg-burgundy/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users size={32} className="text-burgundy" />
              </div>
              <h3 className="font-serif text-xl font-bold text-burgundy mb-3">Famiglia</h3>
              <p className="text-wood-brown font-serif text-sm">
                Tre generazioni al servizio della comunità
              </p>
            </div>

            <div className="text-center p-6 bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1">
              <div className="w-16 h-16 bg-burgundy/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart size={32} className="text-burgundy" />
              </div>
              <h3 className="font-serif text-xl font-bold text-burgundy mb-3">Tradizione</h3>
              <p className="text-wood-brown font-serif text-sm">
                Ricette romagnole tramandate dal 1957
              </p>
            </div>

            <div className="text-center p-6 bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1">
              <div className="w-16 h-16 bg-burgundy/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock size={32} className="text-burgundy" />
              </div>
              <h3 className="font-serif text-xl font-bold text-burgundy mb-3">Artigianalità</h3>
              <p className="text-wood-brown font-serif text-sm">
                Lavorazione manuale e cura dei dettagli
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Products Preview Section */}
      <section className="py-20 bg-beige">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-burgundy mb-4">
              I Nostri Prodotti
            </h2>
            <p className="text-lg text-wood-brown font-serif max-w-2xl mx-auto">
              Scopri la nostra selezione di salumi stagionati, carni fresche e preparati pronti
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Cured Meats */}
            <Link to="/prodotti" className="group">
              <div className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                <div className="aspect-[4/3] overflow-hidden">
                  <img 
                    src={products.curedMeats.image} 
                    alt={products.curedMeats.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                </div>
                <div className="p-6">
                  <h3 className="font-serif text-2xl font-bold text-burgundy mb-3">
                    {products.curedMeats.title}
                  </h3>
                  <p className="text-wood-brown font-serif text-sm mb-4 leading-relaxed">
                    {products.curedMeats.description}
                  </p>
                  <div className="inline-flex items-center text-burgundy font-serif font-medium group-hover:gap-2 transition-all">
                    Scopri di più
                    <ArrowRight size={18} className="ml-1 transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              </div>
            </Link>

            {/* Fresh Meats */}
            <Link to="/prodotti" className="group">
              <div className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                <div className="aspect-[4/3] overflow-hidden">
                  <img 
                    src={products.freshMeats.image} 
                    alt={products.freshMeats.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                </div>
                <div className="p-6">
                  <h3 className="font-serif text-2xl font-bold text-burgundy mb-3">
                    {products.freshMeats.title}
                  </h3>
                  <p className="text-wood-brown font-serif text-sm mb-4 leading-relaxed">
                    {products.freshMeats.description}
                  </p>
                  <div className="inline-flex items-center text-burgundy font-serif font-medium group-hover:gap-2 transition-all">
                    Scopri di più
                    <ArrowRight size={18} className="ml-1 transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              </div>
            </Link>

            {/* Preparations */}
            <Link to="/prodotti" className="group">
              <div className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                <div className="aspect-[4/3] overflow-hidden">
                  <img 
                    src={products.preparations.image} 
                    alt={products.preparations.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                </div>
                <div className="p-6">
                  <h3 className="font-serif text-2xl font-bold text-burgundy mb-3">
                    {products.preparations.title}
                  </h3>
                  <p className="text-wood-brown font-serif text-sm mb-4 leading-relaxed">
                    {products.preparations.description}
                  </p>
                  <div className="inline-flex items-center text-burgundy font-serif font-medium group-hover:gap-2 transition-all">
                    Scopri di più
                    <ArrowRight size={18} className="ml-1 transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-burgundy text-cream relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 border-4 border-cream rounded-full" />
          <div className="absolute bottom-10 right-10 w-40 h-40 border-4 border-cream rounded-full" />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-serif text-4xl md:text-5xl font-bold mb-6">
              Vieni a Trovarci
            </h2>
            <p className="text-xl font-serif mb-10 leading-relaxed text-cream/90">
              Ti aspettiamo nel nostro salumificio a Santarcangelo di Romagna per farti scoprire i sapori autentici della tradizione romagnola.
            </p>
            <Link 
              to="/contatti"
              className="inline-flex items-center bg-cream text-burgundy px-10 py-4 rounded-md font-serif font-medium text-lg transition-all duration-300 hover:bg-beige hover:shadow-xl hover:scale-105 group"
            >
              Dove Siamo
              <ArrowRight size={20} className="ml-2 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
